import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { addNotification } from "store/slices/uiSlice";
import axios from "axios";
import {
  SearchBar,
  Table,
  ToggleButton,
  Button,
  Selector,
} from "globalComponents";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { errorHandler } from "globalResources/util";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faEnvelope,
  faEnvelopeOpen,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import styled from "styled-components";

import { RowType } from "globalComponents/table";
import { getTheme } from "Theme";

const theme = getTheme();

const BoldRow = styled.div`
  font-size: 16px;
  font-weight: 600px;
`;

export const statusTags = {
  all: "All",
  read: "Read",
  unread: "Unread",
};

const typeTags = {
  all: "All",
  user: "User",
  system: "System",
};


export const getStatusIcon = (status) => {
  const { read, unread } = statusTags;
  const statuses = {
    [read]: { icon: faEnvelopeOpen, color: theme.MAIN_COLOR },
    [unread]: { icon: faEnvelope, color: "green" },
  };

  switch (status) {
    case statusTags.all:
      return statuses;
    case statusTags.read:
      return statuses[read];
    case statusTags.unread:
      return statuses[unread];
    default:
      return statuses[unread];
  }
};

const headers = [
  {
    label: "Notification",
    width: "80%",
    render: (row) => <div>{row.subject}</div>,
    sortBy: "subject",
  },
  {
    label: "From",
    width: "10%",
    centered: true,
    sortBy: "sentByName",
    render: (row) => <BoldRow>{row.sentByName}</BoldRow>,
  },
  {
    label: "Received At",
    width: "10%",
    centered: true,
    sortBy: "createdAt",
    render: (row) => (
      <BoldRow>{moment(row.createdAt).format("MM/DD/YYYY-hh:mm a")}</BoldRow>
    ),
  },
];

const FilterBar = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 5px;
  gap: 20px;
`;

const FilterWrap = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
`;

const RefreshButton = styled(Button)`
  width: 33px;
  border-radius: 180px;
  right: 5vw;
  height: 33px;
`;

const NotificationsList = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const history = useHistory();

  const cachedFilters = location?.state?.cachedFilters
    ? location?.state?.cachedFilters
    : null;

  const [notifications, setNotifications] = useState([]);

  const [uniqueSenders, setUniqueSenders] = useState(["All","System"]);

  const [error, setError] = useState();

  const [archived, setArchived] = useState(
    cachedFilters ? cachedFilters.archived : false,
  );

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState(
    cachedFilters ? cachedFilters.search : "",
  );

  const [pagination, setPagination] = useState(
    cachedFilters
      ? { ...cachedFilters.pagination }
      : {
          page: 1,
          limit: 20,
          total: 0,
        },
  );

  const [sort, setSort] = useState(
    cachedFilters
      ? { ...cachedFilters.sort }
      : { sort: "createdAt", direction: -1 },
  );

  const [filters, setFilters] = useState(
    cachedFilters
      ? { ...cachedFilters.filters }
      : {
          type: typeTags.all,
          status: statusTags.all,
          sentByName: ["All"]
        },
  );

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      let res = await axios.get(
        "/notifications", 
        {
          params: {
            search,
            page: pagination.page,
            limit: pagination.limit,
            showArchived: archived,
            ...(filters.type !== typeTags.all
              ? { type: filters.type }
              : null),
            ...(filters.status !== statusTags.all
              ? { status: filters.status }
              : null),
            ...(filters.sentByName !== "All"
              ? { sentByName: filters.sentByName}
              : null),
            ...sort,
          },
        }
      );
      setNotifications(res.data.notifications);
      setPagination((old) => ( { ...old, total: res.data.total}));
    } catch (err) {
      const { message } = errorHandler(err);
      dispatch(addNotification({ type: "error", message }));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update unique senders list every time notifications is updated without 
   * removing previously seen senders when filters are applied.
   */
  useEffect( () => {
    var updatedUniqueSenders = [];
    notifications.forEach((n) => {
      if ( n.sentByName && 
        !uniqueSenders.includes(n.sentByName)
      ) {
        updatedUniqueSenders.push(n.sentByName);
      }
    });

    uniqueSenders.forEach((name) => {
      if(name !== "All" && 
        name !== "System" &&
        !updatedUniqueSenders.includes(name)
      ) {
        updatedUniqueSenders.push(name);
      }
    });

    updatedUniqueSenders.sort();
    updatedUniqueSenders.splice(0, 0, "System");
    updatedUniqueSenders.splice(0, 0, "All");

    setUniqueSenders(updatedUniqueSenders);

  }, [notifications]);

  /**
  * ESLint correctly determines that fetchNotifications is a missing dependency, but because the app requires different conditions for
  * fetching notifications, either instantly or after a 1 second delay, there is no way to resolve this warning without making the code
  * more complicated. 
  * 
  * There are no errors or side effects that result from this warning.
  */
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchNotifications();
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [ search ]);

  useEffect(() => {
    fetchNotifications(); 
  }, [ 
    sort, 
    pagination.page, 
    pagination.limit, 
    filters.type, 
    filters.status,
    filters.sentByName,
  ]); 

  const handleNotificationClick = async (notification) => {
    // Mark the notification as read in the server. Follow the link.
    try {
      await axios.patch("/notification/read", {
        data: {
          notificationId: notification._id
        },
      });
    } catch ( err ) {
      const { message } = errorHandler(err);
      dispatch(addNotification({ type: "error", message }));
    }

    history.push({
      pathname: `${notification.link}`,
      state: {
        cachedFilters: { pagination, sort, filters, search, archived }
      },
    });
  };

  return (
    <>
      <FilterBar>
        <SearchBar
          style={{
            width: "50%",
            margin: "auto",
          }}
          onSearch={(v) => {
            setSearch(v);
            setPagination((old) => ({ ...old, page: 1 }));
          }}
          search={search}
        />
        <FilterWrap>
          <Selector
            options={Object.values(typeTags).map((label) => ({
              value: label,
              label,
            }))}
            onSelect={(val) => setFilters((old) => ({ ...old, type: val }))}
            label={"Type"}
            style={{ width: "240px" }}
            value={filters.type}
          />
          <Selector
            options={Object.values(statusTags).map((label) => ({
              value: label,
              label,
            }))}
            onSelect={(val) => setFilters((old) => ({ ...old, status: val }))}
            label={"Status"}
            style={{ width: "240px" }}
            value={filters.status}
          />
          <Selector 
            options={uniqueSenders.map((label) => ({
              value: label,
              label,
            }))}
            onSelect={(val) => setFilters((old) => ({...old, sentByName: val }))}
            label={"From"}
            style={{ width: "240px" }}
            values={uniqueSenders}
          />
          <ToggleButton
            label="Show Archived"
            onChange={() => setArchived(!archived)}
            value={archived}
          />
          <RefreshButton onClick={() => fetchNotifications()}>
            <FontAwesomeIcon icon={faArrowsRotate} />
          </RefreshButton>
        </FilterWrap>
      </FilterBar>
      <Table
        data={notifications}
        loading={loading === true ? true : undefined}
        headers={headers}
        pagination={pagination}
        onClick={(row) => {
          handleNotificationClick(row);
        }}
        onPage={(page) => setPagination((old) => ({ ...old, page }))}
        onLimit={(limit) => {
          setPagination((old) => ({ ...old, limit }));
        }}
        sortBy={sort.sort}
        sortDirection={sort.direction}
        onSort={(by, direction) => {
          if(by === "createdAt") setSort({ sort: by, direction })
        }}
        rowType={RowType.notification}
      />
    </>
  );
};

export default NotificationsList;
